/**
 * OLI Gateway - LLM Client
 *
 * Client for LLM chat completions via oli-gateway.
 * Endpoint: POST /v1/llm/chat/completions
 */

import { config } from "../config";

// Available models (from GET /v1/llm/models)
export type LlmModel = "gpt-4o-mini" | "gpt-4o" | "gpt-4.1" | "gpt-4.1-mini" | "gpt-4.1-nano" | "gemini-2.5-flash" | "claude-sonnet-4-5-20250929" | "claude-haiku-4-5-20251001";

// Model options for UI dropdowns
export const LLM_MODELS: { id: LlmModel; label: string }[] = [
  { id: "gpt-4o-mini", label: "GPT-4o Mini" },
  { id: "gpt-4o", label: "GPT-4o" },
  { id: "gpt-4.1", label: "GPT-4.1" },
  { id: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
  { id: "gpt-4.1-nano", label: "GPT-4.1 Nano" },
  { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { id: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5" },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
];

// Chat message
export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

// Chat request options
export interface ChatOptions {
  model?: LlmModel;
  temperature?: number;
  maxTokens?: number;
  metadata?: Record<string, string>;
  timeout?: number; // in milliseconds, default 180000 (3 min)
}

// Chat response (OpenAI format)
export interface ChatResponse {
  id: string;
  choices: {
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }[];
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

class OliLlmClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.gateway.baseUrl;
  }

  /**
   * Send a chat completion request
   */
  async chat(
    messages: ChatMessage[],
    options: ChatOptions = {}
  ): Promise<ChatResponse> {
    const timeoutSeconds = Math.floor((options.timeout ?? 180000) / 1000); // Default 3 min
    const body = {
      model: options.model || "gpt-4o-mini",
      messages,
      temperature: options.temperature ?? 0.7,
      timeout: timeoutSeconds, // API timeout in seconds
      ...(options.maxTokens && { max_tokens: options.maxTokens }),
      ...(options.metadata && { metadata: this.buildMetadata(options.metadata) }),
    };

    // Use proxy in development to avoid CORS
    const isDev = window.location.hostname === "localhost";
    const url = isDev ? "/api/llm/chat/completions" : `${this.baseUrl}/v1/llm/chat/completions`;
    console.log("[OliLlmClient] POST", url, isDev ? "(via proxy)" : "");
    console.log("[OliLlmClient] Body:", JSON.stringify(body, null, 2));

    // Setup timeout with AbortController (slightly longer than API timeout)
    const fetchTimeout = (timeoutSeconds + 10) * 1000; // API timeout + 10s buffer
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), fetchTimeout);
    console.log("[OliLlmClient] API timeout:", timeoutSeconds, "s, fetch timeout:", fetchTimeout, "ms");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log("[OliLlmClient] Response:", response.status, response.statusText);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error("[OliLlmClient] Error response:", error);
        const errorMsg = error.error?.message || error.detail || error.message || `HTTP ${response.status}`;
        throw new OliLlmError(errorMsg, response.status);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("[OliLlmClient] Fetch error:", error);
      if (error instanceof OliLlmError) {
        throw error;
      }
      // Handle abort/timeout
      if (error instanceof Error && error.name === "AbortError") {
        throw new OliLlmError(`Timeout após ${timeoutSeconds}s - tente novamente`, 408);
      }
      // Network error, CORS, etc.
      throw new OliLlmError(
        `Network error: ${error instanceof Error ? error.message : "Failed to fetch"}`,
        0
      );
    }
  }

  /**
   * Extract content from chat response
   */
  getContent(response: ChatResponse): string {
    return response.choices[0]?.message?.content || "";
  }

  /**
   * Build metadata with app info
   */
  private buildMetadata(
    extra: Record<string, string>
  ): Record<string, string> {
    return {
      app: config.app.name,
      ...extra,
    };
  }

  /**
   * List available models
   */
  async listModels(): Promise<{ id: string; name: string }[]> {
    const response = await fetch(`${this.baseUrl}/v1/llm/models`);

    if (!response.ok) {
      throw new OliLlmError(`HTTP ${response.status}`, response.status);
    }

    return response.json();
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }
}

/**
 * Custom error class for OLI LLM API errors
 */
export class OliLlmError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = "OliLlmError";
  }
}

// Singleton instance
export const oliLlmClient = new OliLlmClient();
